import { useEffect, useRef, useState } from 'react';
import { getToken } from '@/api/client';

/**
 * Vision structured-extraction result card. Rendered under an assistant
 * message that carries a `<!-- brew:vision taskId=… -->` marker (emitted by the
 * chat fork). Polls /api/vision/tasks/:id until terminal, then fetches the
 * result and renders the fields. No new deps — plain hooks + fetch, mirroring
 * how Chat.tsx authenticates with getToken().
 */

interface VField {
  name: string;
  value: string | null;
  confidence: string | number | null;
  disputed?: boolean;
  escalated?: boolean;
  approved?: boolean;
}
interface ResultPayload {
  taskId: string;
  status: string;
  confidence: number | null;
  fields?: VField[];
  ready?: boolean;
}

const TERMINAL = ['verified', 'partial', 'review_required', 'vision_qa', 'cancelled'];

async function vfetch(path: string): Promise<Response> {
  const t = getToken();
  return fetch(path, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
}

function badge(status: string): { label: string; color: string } {
  switch (status) {
    case 'verified': return { label: 'Verified', color: '#0F6E56' };
    case 'partial': return { label: 'Partial', color: '#8a6d00' };
    case 'review_required': return { label: 'Needs review', color: '#9a3412' };
    case 'vision_qa': return { label: 'Q&A', color: '#534AB7' };
    case 'cancelled': return { label: 'Cancelled', color: '#73726c' };
    default: return { label: status, color: '#73726c' };
  }
}

export function VisionResult({ taskId }: { taskId: string }) {
  const [status, setStatus] = useState('queued');
  const [data, setData] = useState<ResultPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    async function poll(): Promise<void> {
      attempts++;
      try {
        const r = await vfetch(`/api/vision/tasks/${taskId}`);
        if (r.status === 404) { setError('Vision is unavailable.'); return; }
        if (!r.ok) throw new Error(`status ${r.status}`);
        const t = await r.json();
        if (cancelled) return;
        setStatus(t.status);
        if (t.terminal || TERMINAL.includes(t.status)) {
          const rr = await vfetch(`/api/vision/tasks/${taskId}/result`);
          if (rr.ok && !cancelled) setData(await rr.json());
          return; // done — stop polling
        }
      } catch {
        if (!cancelled && attempts > 30) { setError('Extraction timed out.'); return; }
      }
      if (!cancelled && attempts < 90) timer.current = window.setTimeout(poll, 2000);
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [taskId]);

  if (error) {
    return <div className="vision-card vision-card--error" style={{ marginTop: 8, fontSize: 13, color: '#9a3412' }}>{error}</div>;
  }

  const b = badge(status);
  const inProgress = !TERMINAL.includes(status);
  const fields = data?.fields ?? [];

  return (
    <div
      className="vision-card"
      style={{ marginTop: 8, border: '1px solid #e2e0d8', borderRadius: 10, padding: 12, background: '#faf9f5' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: fields.length ? 8 : 0 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>📄 Document extraction</span>
        <span style={{ fontSize: 12, color: '#fff', background: b.color, borderRadius: 6, padding: '1px 8px' }}>{b.label}</span>
        {inProgress ? <span style={{ fontSize: 12, color: '#73726c' }}>· {status}…</span> : null}
        {data?.confidence != null ? <span style={{ fontSize: 12, color: '#73726c' }}>· {data.confidence}%</span> : null}
      </div>

      {status === 'review_required' ? (
        <div style={{ fontSize: 12, color: '#9a3412', marginBottom: fields.length ? 8 : 0 }}>
          Some fields need a human review before they can be trusted.
        </div>
      ) : null}

      {fields.length ? (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {fields.map((f) => (
              <tr key={f.name} style={{ borderTop: '1px solid #efece3' }}>
                <td style={{ padding: '4px 8px 4px 0', color: '#73726c', whiteSpace: 'nowrap' }}>{f.name}</td>
                <td style={{ padding: '4px 0', fontWeight: 500 }}>
                  {f.value ?? <span style={{ color: '#b3b1a8' }}>—</span>}
                  {f.disputed ? <span title="disputed" style={{ marginLeft: 6 }}>⚠️</span> : null}
                  {f.escalated ? <span title="escalated to larger model" style={{ marginLeft: 4 }}>⤴️</span> : null}
                </td>
                <td style={{ padding: '4px 0 4px 8px', textAlign: 'right', color: '#b3b1a8', whiteSpace: 'nowrap' }}>
                  {f.confidence != null ? `${Number(f.confidence)}%` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : inProgress ? (
        <div style={{ fontSize: 12, color: '#73726c' }}>Working on it…</div>
      ) : null}
    </div>
  );
}

/** Extract the vision taskId from an assistant message's marker, or null. */
export function extractVisionTaskId(content: string): string | null {
  const m = /<!--\s*brew:vision\s+taskId=([\w-]+)\s*-->/.exec(content || '');
  return m?.[1] ?? null;
}
